import { useContext, useEffect, useState, useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import {
  ActionIcon,
  Button,
  Container,
  Paper,
  Text,
  Stack,
  Group,
  Badge,
  Card,
  Title,
  Transition,
  Box,
  Tooltip,
} from "@mantine/core";
import {
  IconGripVertical,
  IconPlus,
  IconCheck,
  IconPencil,
  IconTrash,
} from "@tabler/icons-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

import { AuthContext } from "../../services/context";
import { getCurrentWorkspace } from "../../services/userConfig";
import { useGetSponsorLevels, usePatchSponsorLevels } from "../../api/sponsors";
import { useGetEvent } from "../../api/event";
import LocalizedInput from "../common/LocalizedInput";
import { TextInput } from "react-hook-form-mantine";

function SponsorLevels() {
  const { user } = useContext(AuthContext);
  const workspaceId = user?.config.adminUI?.activeWorkspaceId;
  const { eventId } = getCurrentWorkspace(user);

  const { data: sponsorLevels } = useGetSponsorLevels({ eventId });
  const sortedLevels = useMemo(
    () => sponsorLevels?.sort((a, b) => a.order - b.order),
    [sponsorLevels]
  );
  const { data: event } = useGetEvent(eventId);

  const { mutate, isPending } = usePatchSponsorLevels();

  const defaultValues = { levels: [], newLevelNameI18n: {} };
  const { control, getValues, setValue, reset, watch } = useForm({
    defaultValues,
  });
  const levels = watch("levels");
  const [editingLevelId, setEditingLevelId] = useState(null);
  const [showAddInput, setShowAddInput] = useState(false);
  const [error, setError] = useState("");

  const mainLanguage = event?.language || "en";
  const otherLanguages = event?.secondaryLanguages || [];

  const parseNameI18n = useCallback(
    (value) => {
      if (!value) return {};
      if (typeof value === "string") {
        try {
          return JSON.parse(value) || {};
        } catch (_e) {
          return { [mainLanguage]: value };
        }
      }
      return value;
    },
    [mainLanguage]
  );

  const normalizeLevels = useCallback(
    (levels) =>
      (levels || []).map((l) => ({
        id: l.id,
        order: l.order,
        nameI18n:
          parseNameI18n(l.nameI18n) ||
          (l.name ? { [mainLanguage]: l.name } : {}),
        used: l.used,
      })),
    [parseNameI18n, mainLanguage]
  );

  const handleEditClick = (level) => {
    setEditingLevelId(level.id);
    setError("");
  };

  const handleSave = () => {
    const currentLevels = getValues("levels");
    const edited = currentLevels.find((l) => l.id === editingLevelId);
    const editedPrimary = (edited?.nameI18n?.[mainLanguage] || "").trim();
    if (!editedPrimary) {
      setError("Primary language name is required");
      return;
    }
    const duplicate = currentLevels.find(
      (l) =>
        l.id !== editingLevelId &&
        (l.nameI18n?.[mainLanguage] || "").trim().toLowerCase() ===
          editedPrimary.toLowerCase()
    );
    if (duplicate) {
      setError(
        <>
          Level name <strong>{duplicate.nameI18n?.[mainLanguage]}</strong>{" "}
          already exists.
          <br />
          Please choose a different name.
        </>
      );
      return;
    }
    setEditingLevelId(null);
    setError("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSave();
    }
  };

  const updateOrder = (updatedLevels) => {
    return updatedLevels.map((level, idx) => ({ ...level, order: idx + 1 }));
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;

    const currentLevels = getValues("levels");
    const items = Array.from(currentLevels);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setValue("levels", updateOrder(items));
  };

  const handleAddLevel = () => {
    const nameI18n = getValues("newLevelNameI18n") || {};
    const primary = (nameI18n?.[mainLanguage] || "").trim();
    if (!primary) {
      setError("Level name cannot be empty");
      return;
    }

    const levelAlreadyExists = (levels || []).find(
      (level) =>
        (level.nameI18n?.[mainLanguage] || "").trim().toLowerCase() ===
        primary.toLowerCase()
    );
    if (levelAlreadyExists) {
      setError(
        <>
          Level name{" "}
          <strong>{levelAlreadyExists.nameI18n?.[mainLanguage]}</strong> already
          exists.
          <br />
          Please choose a different name.
        </>
      );
      return;
    }

    const cleaned = Object.fromEntries(
      Object.entries(nameI18n).filter(
        ([, v]) => typeof v === "string" && v.trim() !== ""
      )
    );

    const newLevel = {
      nameI18n: cleaned,
      order: (levels?.length || 0) + 1,
    };

    const updatedLevels = updateOrder([...(levels || []), newLevel]);
    setValue("levels", updatedLevels);
    setValue("newLevelNameI18n", {});
    setShowAddInput(false);
    setError("");
  };

  const handleCancelAdd = () => {
    setValue("newLevelNameI18n", {});
    setShowAddInput(false);
  };

  /** Removes a level from the local form state. Actual deletion happens on save. */
  const handleDeleteLevel = (levelToRemove) => {
    const currentLevels = getValues("levels");
    const updatedLevels = currentLevels.filter(
      (l) => (l.id || l.order) !== (levelToRemove.id || levelToRemove.order)
    );
    setValue("levels", updateOrder(updatedLevels));
    setError("");
  };

  /** Submits all changes (add, edit, delete, reorder) to the server. */
  const handleSubmit = () => {
    const payloadLevels = (getValues("levels") || []).map((l) => ({
      id: l.id,
      order: l.order,
      nameI18n: l.nameI18n,
    }));
    mutate(
      { levels: payloadLevels, eventId, workspaceId },
      {
        onError: (err) => setError(err?.message || "Failed to save changes"),
      }
    );
  };

  // Sync form state when server data changes
  useEffect(() => {
    if (sortedLevels) {
      reset({ levels: normalizeLevels(sortedLevels), newLevelNameI18n: {} });
    }
  }, [sortedLevels, reset, normalizeLevels]);

  const initialLevelsJson = JSON.stringify(normalizeLevels(sortedLevels));
  const hasChanged = JSON.stringify(levels || []) !== initialLevelsJson;

  const primaryName = (level) =>
    level?.nameI18n?.[mainLanguage] ||
    level?.nameI18n?.en ||
    Object.values(level?.nameI18n || {})[0] ||
    "Untitled";

  return (
    <Container size={600}>
      <Stack gap="md" mt="md">
        <Paper withBorder shadow="sm" p="lg" radius="md">
          <Group justify="space-between" mb="md">
            <div>
              <Title order={2}>Sponsor Levels</Title>
              <Text size="sm" c="dimmed" mt={4}>
                Drag and drop to reorder. Higher levels appear first.
              </Text>
            </div>
            <Button
              leftSection={<IconPlus size={24} />}
              onClick={() => setShowAddInput(true)}
              disabled={showAddInput}
            >
              Add Level
            </Button>
          </Group>

          {/* Add new level card */}
          <Transition
            mounted={showAddInput}
            transition="slide-down"
            duration={200}
          >
            {(styles) => (
              <Card
                withBorder
                shadow="sm"
                mb="md"
                p="md"
                style={{
                  ...styles,
                  borderColor: "var(--mantine-color-blue-6)",
                }}
              >
                <Stack gap="sm">
                  <Group justify="space-between">
                    <Text fw={500} size="sm" c="blue">
                      New Sponsor Level
                    </Text>
                  </Group>
                  <LocalizedInput
                    control={control}
                    fields={[
                      {
                        nameBase: "newLevelNameI18n",
                        label: "Level Name",
                        component: TextInput,
                        props: (lang) => ({
                          placeholder: `Level name (${lang.toUpperCase()})`,
                          withAsterisk: lang === mainLanguage,
                          onKeyDown: (e) => {
                            if (e.key === "Enter") handleAddLevel();
                            if (e.key === "Escape") handleCancelAdd();
                          },
                        }),
                      },
                    ]}
                    mainLanguage={mainLanguage}
                    otherLanguages={otherLanguages}
                  />
                  <Group justify="flex-end" gap="xs">
                    <Button
                      variant="subtle"
                      color="gray"
                      onClick={handleCancelAdd}
                    >
                      Cancel
                    </Button>
                    <Button
                      leftSection={<IconCheck size={24} />}
                      onClick={handleAddLevel}
                    >
                      Add Level
                    </Button>
                  </Group>
                </Stack>
              </Card>
            )}
          </Transition>

          {/* Error message */}
          {error && (
            <Paper
              withBorder
              p="sm"
              mb="md"
              bg="red.0"
              style={{ borderColor: "var(--mantine-color-red-6)" }}
            >
              <Text c="red" size="sm">
                {error.message || error}
              </Text>
            </Paper>
          )}

          {/* Drag and drop sponsor levels */}
          {levels?.length > 0 ? (
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="sponsor-levels">
                {(provided) => (
                  <Stack
                    gap="sm"
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    {levels.map((level, index) => (
                      <Draggable
                        key={level.id || level.order}
                        draggableId={String(level.id || level.order)}
                        index={index}
                        isDragDisabled={editingLevelId === level.id}
                      >
                        {(provided, snapshot) => (
                          <Card
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            withBorder
                            shadow={snapshot.isDragging ? "lg" : "sm"}
                            p="md"
                            style={{
                              ...provided.draggableProps.style,
                              backgroundColor: snapshot.isDragging
                                ? "var(--mantine-color-blue-0)"
                                : undefined,
                              cursor:
                                editingLevelId === level.id
                                  ? "default"
                                  : "grab",
                            }}
                          >
                            <Group justify="space-between" wrap="nowrap">
                              <Group gap="md" style={{ flex: 1 }}>
                                <Box
                                  {...provided.dragHandleProps}
                                  style={{
                                    cursor:
                                      editingLevelId === level.id
                                        ? "default"
                                        : "grab",
                                    opacity:
                                      editingLevelId === level.id ? 0.3 : 1,
                                  }}
                                >
                                  <IconGripVertical
                                    size={20}
                                    color="var(--mantine-color-gray-5)"
                                  />
                                </Box>

                                <Badge
                                  size="lg"
                                  variant="light"
                                  color="blue"
                                  style={{ minWidth: 40 }}
                                >
                                  #{level.order}
                                </Badge>

                                {editingLevelId === level.id ? (
                                  <Box style={{ flex: 1 }}>
                                    <LocalizedInput
                                      control={control}
                                      fields={[
                                        {
                                          nameBase: `levels.${index}.nameI18n`,
                                          label: "Name",
                                          component: TextInput,
                                          props: (lang) => ({
                                            placeholder: `Level name (${lang.toUpperCase()})`,
                                            withAsterisk: lang === mainLanguage,
                                            onKeyDown: handleKeyDown,
                                            size: "sm",
                                          }),
                                        },
                                      ]}
                                      mainLanguage={mainLanguage}
                                      otherLanguages={otherLanguages}
                                    />
                                  </Box>
                                ) : (
                                  <Text fw={500} style={{ flex: 1 }}>
                                    {primaryName(level)}
                                  </Text>
                                )}
                              </Group>

                              <Group gap="xs">
                                {editingLevelId === level.id ? (
                                  <ActionIcon
                                    color="green"
                                    variant="light"
                                    onClick={handleSave}
                                    size="lg"
                                  >
                                    <IconCheck size={24} />
                                  </ActionIcon>
                                ) : (
                                  <ActionIcon
                                    color="blue"
                                    variant="light"
                                    onClick={() => handleEditClick(level)}
                                    size="lg"
                                  >
                                    <IconPencil size={24} />
                                  </ActionIcon>
                                )}
                                <Tooltip
                                  label="Sponsor level cannot be deleted because it is in use"
                                  disabled={!level.used}
                                  multiline
                                  w={200}
                                >
                                  <ActionIcon
                                    color="red"
                                    variant="light"
                                    onClick={() => handleDeleteLevel(level)}
                                    disabled={
                                      editingLevelId === level.id || level.used
                                    }
                                    size="lg"
                                  >
                                    <IconTrash size={24} />
                                  </ActionIcon>
                                </Tooltip>
                              </Group>
                            </Group>
                          </Card>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Stack>
                )}
              </Droppable>
            </DragDropContext>
          ) : (
            <Paper withBorder p="xl" style={{ textAlign: "center" }}>
              <Text c="dimmed" size="sm">
                No sponsor levels yet. Click "Add Level" to create your first
                one.
              </Text>
            </Paper>
          )}

          {/* Save section */}
          {hasChanged && (
            <Paper
              withBorder
              p="sm"
              mt="md"
              bg="yellow.0"
              style={{ borderColor: "var(--mantine-color-yellow-6)" }}
            >
              <Group justify="space-between">
                <Text size="sm" c="yellow.9" fw={500}>
                  You have unsaved changes
                </Text>
                <Button onClick={handleSubmit} loading={isPending}>
                  Save Changes
                </Button>
              </Group>
            </Paper>
          )}
        </Paper>
      </Stack>
    </Container>
  );
}

export default SponsorLevels;
